import React, { FC, memo, useEffect, useState } from 'react'

import { FormControl, MenuItem, Select, styled } from '@mui/material'
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { ObjectTypeEnum, SubConditionOperator, SubConditionType } from '../../types/SubCondition'
import PixelSingleSelect from '../../pixel/PixelSingleSelect';
import AutoComplete from '../../pixel/AutoComplete';
import Statement from './Statement';
import { useCustomField, useCustomFieldOptions } from '../../context/CustomField/customFieldHooks';
import { CustomFieldTypeFromService, SELECTABLE_FIELD_TYPES_MAP } from '../../context/CustomField/CustomFieldTypes';
import { useMultipleLocalizedText } from '../../context/Localization/localizationHooks';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import { deleteSubCondition, updateSubConditionBaseOperator, updateSubConditionFieldEId, updateSubConditionFieldType, updateSubConditionOperator, updateSubConditionValues, validateSubCondition } from '../../store/groupConditionsSlice';
import { useViewModeReadOnly } from '../../context/ViewMode/viewModeHooks'

const RootContainer = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
}))

const RowContainer = styled('div', {
    shouldForwardProp: (propName) => propName !== 'flexLeft' && propName !== 'flexRight',
})(({ flexRight, flexLeft }: { flexRight?: boolean | undefined , flexLeft?: boolean | undefined }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: (flexRight ? 'end' : 'space-between'),
    width: '100%',
}))

const LeftEndContainer = styled('div', {
    shouldForwardProp: (propName) => propName !== 'flexLeft' && propName !== 'flexRight',
})(({ flexRight, flexLeft }: { flexRight?: boolean | undefined , flexLeft?: boolean | undefined }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    width: '100%',
}))
const SpanOverride = styled('span')(() => ({
    color: '#263238', 
    fontWeight: 600, 
    fontSize: 16, 
    fontFamily: 'Open Sans',
    margin: '0px 8px',
}))
export interface SubConditionContainerProps {
    index: number
    groupIndex: number
    showDelete: boolean
}
const Container: FC<SubConditionContainerProps> = (props) => {
    const [localErrors, setLocalErrors] = useState<ReturnType<typeof validateSubCondition>>()
    const isReadOnly = useViewModeReadOnly()
    const validated = useAppSelector(({ groupConditions }) => groupConditions.validated)
    const subCondition = useAppSelector(
        ({ groupConditions }) => groupConditions.groupConditions[props.groupIndex].conditions[props.index]
    )
    const { 
        objectType,
        fieldEId,
        operator,
        statement,
    } = subCondition
    const dispatch = useAppDispatch()

    const { 
        IF, 
        ADMINOPERATORAND, 
        ADMINOPERATOROR,
        ADMINSELECTFIELDTYPE,
        ADMINFIELDVALUE,
        ADMINSELECTFIELDVALUE,
        ADMINNORESULTS,
    } = useMultipleLocalizedText()

    const customFieldDisabled = !objectType || objectType === ObjectTypeEnum.NOT_SET

    const customField = useCustomField(objectType, fieldEId)
    const customFieldOptionsAvailable = useCustomFieldOptions(objectType)

    const selectFieldsWidth = 320

    useEffect(() => {
        if (validated) {
            const subConditionErrors = validateSubCondition(subCondition)
            setLocalErrors(subConditionErrors)
        }
    }, [validated, subCondition])

    return (
        <RootContainer>
            {operator !== null && (
                <FormControl 
                    size="small" 
                    sx={{ m: 1, height: '40px', margin: '48px 8px' }} 
                >
                    <Select
                        autoWidth
                        value={operator}
                        onChange={(e) => dispatch(updateSubConditionBaseOperator({ 
                            groupIndex: props.groupIndex, 
                            subConditionIndex: props.index, 
                            newOperator: e.target.value as SubConditionOperator
                        }))}
                        disabled={isReadOnly}
                    >
                        <MenuItem value="AND">{ADMINOPERATORAND}</MenuItem>
                        <MenuItem value="OR">{ADMINOPERATOROR}</MenuItem>
                    </Select>
                </FormControl>
            )}
            <RowContainer>
                <RowContainer>
                    <SpanOverride>{IF}</SpanOverride>
                    <PixelSingleSelect 
                        autoWidth
                        widthPX={selectFieldsWidth}
                        heightPX={40}
                        options={SELECTABLE_FIELD_TYPES_MAP}
                        value={!objectType || objectType === ObjectTypeEnum.NOT_SET ? '' : objectType}
                        onChange={(e) => dispatch(updateSubConditionFieldType({
                            groupIndex: props.groupIndex, 
                            subConditionIndex: props.index, 
                            newFieldType: e.target.value as ObjectTypeEnum
                        }))}
                        selectProps={{
                            autoWidth: true,
                            displayEmpty: true,
                            disabled: isReadOnly
                        }}
                        placeholder={ADMINSELECTFIELDTYPE}
                        hasError={localErrors && localErrors.type === 'objectType'}
                    />
                    <AutoComplete
                        fieldLabel={ADMINFIELDVALUE}
                        placeholder={ADMINSELECTFIELDVALUE}
                        options={customFieldOptionsAvailable}
                        disabled={customFieldDisabled || isReadOnly}
                        value={customField || null}
                        multiple={false}
                        selectedIdKey="id"
                        onKeyStroke={() => {}}
                        getOptionLabel={option => option.name}
                        onSelect={(option: CustomFieldTypeFromService) => {
                            const fieldEId = option ? (option.fieldEId || '') : ''
                            dispatch(updateSubConditionFieldEId({
                                groupIndex: props.groupIndex, 
                                subConditionIndex: props.index, 
                                fieldEId, 
                                fieldType: option.fieldType,
                            }))
                        }}
                        hasError={localErrors && localErrors.type === 'fieldEId'}
                        ariaLabelNoResults={ADMINNORESULTS}
                    />
                </RowContainer>
                <RowContainer flexRight>
                    {
                        props.showDelete && !isReadOnly && (
                            <IconButton color="default" aria-label="Delete Subcondition" onClick={() => dispatch(deleteSubCondition({
                                groupIndex: props.groupIndex, 
                                subConditionIndex: props.index,
                            }))}>
                                <DeleteIcon />
                            </IconButton>
                        )
                    }
                </RowContainer>
            </RowContainer>
            <LeftEndContainer flexLeft>
                {
                    customField && fieldEId && Boolean(objectType) && objectType !== ObjectTypeEnum.NOT_SET &&
                        <Statement 
                            objectType={objectType}
                            fieldEId={fieldEId}
                            fieldType={customField.fieldType}
                            operator={statement.operator}
                            statement={statement}
                            errors={localErrors}
                            onUpdateSubConditionOperator={(operator) => dispatch(updateSubConditionOperator({
                                groupIndex: props.groupIndex,
                                subConditionIndex :props.index, 
                                operator,
                            }))}
                            onUpdateSubConditionValues={(newValues) => dispatch(updateSubConditionValues({
                                groupIndex: props.groupIndex, 
                                subConditionIndex: props.index, 
                                newValues,
                            }))}
                            disabled={isReadOnly}
                        />
                }
            </LeftEndContainer>
        </RootContainer>
    )
}

export const MemoizedContainer = memo(Container, (prevProps, nextProps) => (
    prevProps.index === nextProps.index &&
    prevProps.groupIndex === nextProps.groupIndex &&
    prevProps.showDelete === nextProps.showDelete 
))

export default MemoizedContainer